import { request } from '@umijs/max';

export type PromptApplicablePage = 'workstudio' | 'translation';
export type LibraryPromptMetadata = {
  applicablePages?: PromptApplicablePage[];
  baseDocumentId?: string | null;
};
export type LibraryPrompt = {
  id: string;
  name: string;
  purpose: string;
  content: string;
  applicablePages: PromptApplicablePage[];
  baseDocumentId: string | null;
  contextDocumentIds: string[];
  practiceAreaIds: string[];
};
export function normalizeLibraryPrompt(raw: Record<string, any>): LibraryPrompt {
  return {
    id: String(raw.id ?? raw._id ?? ''),
    name: raw.name ?? '',
    purpose: raw.purpose ?? '',
    content: raw.content ?? '',
    applicablePages: raw.applicablePages ?? raw.applicable_pages ?? [],
    baseDocumentId: raw.baseDocumentId ?? raw.base_document_id ?? null,
    contextDocumentIds: raw.contextDocumentIds ?? raw.context_document_ids ?? [],
    practiceAreaIds: raw.practiceAreaIds ?? raw.practice_area_ids ?? [],
  };
}
export async function getPageLibrary(query: {
  applicablePage: PromptApplicablePage;
  practiceAreaIds?: string[];
  search: string;
  page: number;
  pageSize: number;
}): Promise<{ items: LibraryPrompt[]; total: number }> {
  const { practiceAreaIds, ...params } = query;
  const result = await request('/api/library/prompts', {
    params: {
      ...params,
      ...(practiceAreaIds?.length ? { practiceAreaIds: practiceAreaIds.join(',') } : {}),
    },
  });
  const body = result?.data && !Array.isArray(result.data) ? result.data : result;
  const items = Array.isArray(body) ? body : body?.data ?? body?.items ?? [];
  return {
    items: items.map(normalizeLibraryPrompt),
    total:
      body?.pagination?.totalItems ??
      result?.pagination?.totalItems ??
      body?.total ??
      result?.total ??
      items.length,
  };
}
export type WorkstudioPromptAttachments = {
  baseDocument: { id: string; fileName: string; fileUrl: string } | null;
  contextDocuments: { id: string; fileName: string }[];
};
export async function getWorkstudioPromptAttachments(
  id: string,
): Promise<WorkstudioPromptAttachments> {
  const result = await request(`/api/library/prompts/${id}/workstudio-attachments`);
  return result?.data ?? result;
}

const VISIBILITY_TO_PATH: Record<string, string> = {
  personal: 'personal',
  groups: 'groups',
  firm: 'firm',
  public: 'public',
};

export const getLibrary = async (
  search?: string,
  group?: string | undefined,
  practiceAreaIds?: string[],
  pageSize = 20,
) => {
  const path =
    group && VISIBILITY_TO_PATH[group]
      ? `/api/library/prompts/${VISIBILITY_TO_PATH[group]}`
      : `/api/library/prompts`;
  const params: Record<string, any> = {
    page: 1,
    // cap results for the type-ahead; fetching the whole library freezes the composer
    pageSize,
    search: encodeURIComponent(search || ''),
  };
  if (practiceAreaIds?.length) {
    params.practiceAreaIds = practiceAreaIds.join(',');
  }
  return request(path, { params });
};

export async function updateLibrary(
  id: string,
  data: {
    name?: string;
    purpose?: string;
    content?: string;
    favorite?: boolean;
    tags?: string[];
    notes?: string;
    allowSelfInstruction?: boolean;
    contextDocumentIds?: string[];
    practiceAreaIds?: string[];
  } & LibraryPromptMetadata,
) {
  return request(`/api/library/prompts/${id}`, { data, method: 'PUT' });
}

export async function updatePublishedLibrary(
  id: string,
  data: {
    name?: string;
    purpose?: string;
    content?: string;
    tags?: string[];
    notes?: string;
    allowSelfInstruction?: boolean;
    contextDocumentIds?: string[];
    practiceAreaIds?: string[];
  } & LibraryPromptMetadata,
) {
  return request(`/api/library/prompts/published/${id}`, { data, method: 'PUT' });
}

export async function addLibrary(
  data: {
    name?: string;
    purpose?: string;
    content: string;
    tags?: string[];
    notes?: string;
    allowSelfInstruction?: boolean;
    contextDocumentIds?: string[];
    practiceAreaIds?: string[];
  } & LibraryPromptMetadata,
) {
  return request(`/api/library/prompts`, { data, method: 'POST' });
}

export async function publishLibrary(data: {
  promptId: string;
  visibility: string;
  groupIds?: string[];
}) {
  return request(`/api/library/prompts/publish`, { data, method: 'POST' });
}

export const removeLibrary = async (id: string) => {
  return request(`/api/library/prompts/${id}`, { method: 'DELETE' });
};

export const removePublicLibrary = async (id: string) => {
  return request(`/api/library/prompts/publish/${id}`, { method: 'DELETE' });
};

export const getTags = async () => {
  return request(`/api/library/tags`);
};

export const getPracticeAreas = async () => {
  return request(`/api/library/practice-areas`);
};

export const updateExample = async (id: string, data: any) => {
  return request(`/api/review/checklist/${id}`, { method: 'PUT', data });
};

export const getPromptPublishRequests = async () => {
  return request(`/api/library/prompts/publish-requests`);
};

export type PromptApprovalDecision = {
  revision: number;
  updates?: Parameters<typeof updatePublishedLibrary>[1];
};

export const approvePromptPublishRequest = async (
  id: string,
  decision?: PromptApprovalDecision,
) => {
  return request(`/api/library/prompts/publish-request/${id}/approve`, {
    method: 'POST',
    ...(decision ? { data: decision } : {}),
  });
};

export const rejectPromptPublishRequest = async (id: string, decision?: { revision: number }) => {
  return request(`/api/library/prompts/publish-request/${id}/deny`, {
    method: 'POST',
    ...(decision ? { data: decision } : {}),
  });
};
